import { Table, Tabs } from "antd";
import { map, filter, sortBy, isEmpty } from "lodash";
import "./measures.scss";
import Filter from "./filters/filter";
import { useCallback, useEffect, useRef, useState } from "react";
import reportService from "@services/report.service";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import { TableLocale } from "@components/noDataFound";
import ColumnContentRenderer from "./columnFormat";
import dashboardService from "@services/dashboard.service";
import { useMeasuresStore } from "@stores/measuresStore";
import { usePopulationInsightStore } from "@stores/populationInsightStore";
const { TabPane } = Tabs;

const tabToPageNameMap: Record<string, string> = {
  hedis: "MEASURES-DASHBOARD",
  medication: "MEDICATION-ADHERANCE-MEASURES-DASHBOARD",
  pharmacy: "PHARMACY-GAP-MEASURES-DASHBOARD",
};

const MeasureReport = () => {
  const [initialLoad, setinitialLoad] = useState(true);
  const [loading, setLoading] = useState(true);
  const [columnsLoading, setColumnsLoading] = useState(true);
  const [tableColumn, setTableColumn] = useState<any>([]);
  const [columnsList, setColumnsList] = useState<any>([]);
  const [tableData, setTableData] = useState<any>([]);
  const selectedProvider = useMeasuresStore((state) => state.selectedProvider);
  const selectedPCPVendor = useMeasuresStore((state) => state.selectedPCPVendor);
  const selectedHealthPlan = useMeasuresStore((state) => state.selectedHealthPlan);
  const handleComingFromMembersFlag = usePopulationInsightStore((state) => state.handleComingFromMembersFlag);
  const appliedProvider = useMeasuresStore((state) => state.appliedProvider);
  const setAppliedProvider = useMeasuresStore((state) => state.setAppliedProvider);
  const comingFromMembers = usePopulationInsightStore((state) => state.comingFromMembers);
  const selectedYear = useMeasuresStore((state) => state.selectedYear);
  const setSelectedYear = useMeasuresStore((state) => state.setSelectedYear);
  const selectedTab = useMeasuresStore((state) => state.selectedTab);
  const setSelectedTab = useMeasuresStore((state) => state.setSelectedTab);
  const [activeTabKey, setActiveTabKey] = useState(selectedTab);
  const requestTokenRef = useRef(0);

  const currentYear = new Date().getFullYear();
  const startYear = 2024;
  const years = Array.from({ length: currentYear - startYear + 1 }, (_, index) => ({
    id: currentYear - index,
    value: (currentYear - index).toString(),
  }));

  const fetchData = async (year: string, tabKey: string, healthPlan: any, pcpVendor: any, pcpId: any) => {
    setLoading(true);
    const currentToken = ++requestTokenRef.current;
    let observable;

    if (tabKey === "hedis") {
      observable = dashboardService.getMeasuresReportInfo(year, tabKey, pcpId, pcpVendor, healthPlan);
    } else {
      observable = dashboardService.getMeasuresReportInfo(year, tabKey);
    }

    observable.subscribe({
      next: (v) => {
        if (currentToken === requestTokenRef.current) {
          setTableData(v?.data?.data);
          handleComingFromMembersFlag(false);
          setLoading(false);
        }
      },
      error: (e) => {
        if (currentToken === requestTokenRef.current) {
          console.log(e);
          setLoading(false);
        }
      },
    });
  };

  const fetchTableColumns = useCallback((tabKey: string) => {
      const pageName = tabToPageNameMap[tabKey];
      setColumnsLoading(true);

      reportService.getColumnsList(pageName).subscribe({
        next: (v) => {
          let _columnsNeedToShowInTable = filter(v.data.data?.columns, (item) => {
            return item.isDisplay === "Y";
          });

          setTableColumn(_columnsNeedToShowInTable);
          setColumnsLoading(false);
        },
        error: (e) => {
          console.log(e);
          setColumnsLoading(false);
        },
      });
    },
    [setTableColumn]
  );

  useEffect(() => {
    fetchTableColumns(activeTabKey);
  }, [fetchTableColumns, activeTabKey]);

  useEffect(() => {
    if (!initialLoad) {
      fetchData(selectedYear, activeTabKey, selectedHealthPlan, selectedPCPVendor, selectedProvider);
    }
  }, [activeTabKey]);

  useEffect(() => {
    if (initialLoad && !isEmpty(selectedProvider)) {
      if (comingFromMembers) {
        fetchData(selectedYear, activeTabKey, appliedProvider[0], appliedProvider[1], appliedProvider[2]);
      } else {
        setAppliedProvider([selectedHealthPlan, selectedPCPVendor, selectedProvider]);
        fetchData(selectedYear, activeTabKey, selectedHealthPlan, selectedPCPVendor, selectedProvider);
      }
      setinitialLoad(false);
    }
  }, [initialLoad, selectedPCPVendor, selectedProvider, activeTabKey]);

  useEffect(() => {
    let _columns = map(tableColumn, (item, index: number) => {
      const formatTypes = item.format_type ? item.format_type.split("$|") : [];
      const measureFormatTypes = ["STRING", "DESCRIPTION", "SUBDESCRIPTION"];

      const columnConfig = {
        ...item,
        align: "left",
        ellipsis: true,
        title: item.tableHeaderName,
        dataIndex: item.dataIndex,
        key: item.key,
        width: Number(item.columnWidth),
        sorter: (a: any, b: any) => {
          const valueA = a[item.dataIndex];
          const valueB = b[item.dataIndex];

          if (measureFormatTypes.some((type) => formatTypes.includes(type))) {
            return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
          } else {
            return valueA - valueB;
          }
        },

        render: (value: any, record: any) => {
          return <ColumnContentRenderer value={value} formatTypes={formatTypes} record={record} column={item} selectedYear={selectedYear} tabKey={activeTabKey}/>;
        },
      };

      if (index === 0) {
        columnConfig.width = 700;
      }

      return columnConfig;
    });

    _columns = sortBy(_columns, (item: any) => {
      return item.order;
    });

    setColumnsList(_columns);
  }, [tableColumn, selectedYear]);

  const handleYearChange = (value: string) => {
    setSelectedYear(value);
    fetchData(value, activeTabKey, selectedHealthPlan, selectedPCPVendor, selectedProvider);
  };

  useEffect(() => {
    if (!selectedYear) {
      setSelectedYear(years[0].value);
    }
  }, []);

  const getCurrentTabConfig = () => {
    if (activeTabKey === "hedis") {
      return {
        columns: columnsList,
        data: tableData,
      };
    } else if (activeTabKey === "medication") {
      return {
        columns: columnsList,
        data: tableData,
      };
    } else if (activeTabKey === "pharmacy") {
      return {
        columns: columnsList,
        data: tableData,
      };
    }
    return {
      columns: [],
      data: [],
    };
  };

  return (
    <div className="measure-report">
      <Tabs
        activeKey={activeTabKey}
        onChange={(key) => {
          setActiveTabKey(key);
          setSelectedTab(key);
        }}
        type="card"
        tabBarExtraContent={{
          left: (
            <div className="measure-report__header__title" style={{ fontSize: 20, fontWeight: 600 }}>
              Measures
            </div>
          ),
          right: (
            <Filter
              columns={columnsList}
              tableData={tableData}
              fetchData={fetchData}
              years={years}
              selectedYear={selectedYear}
              onYearChange={handleYearChange}
              isDataLoading={loading}
              tabKey={activeTabKey}
            />
          ),
        }}
        tabBarStyle={{ justifyContent: "flex-end", display: "flex" }}
      >
        <TabPane tab="HEDIS Measures" key="hedis" />
        <TabPane tab="Medication Adherence" key="medication" />
        <TabPane tab="Pharmacy Gap" key="pharmacy" />
      </Tabs>

      <div className="measure-report__body">
        <span className="measure-report__body__subtitle">
          Measurement Year Jan {selectedYear} to {selectedYear === currentYear.toString() ? "Current Month" : `Dec ${selectedYear}`}
        </span>
        <Table
          bordered
          columns={getCurrentTabConfig().columns}
          dataSource={getCurrentTabConfig().data}
          className="measure-report__body__table"
          pagination={false}
          loading={{ spinning: loading || columnsLoading, indicator: <HealthemSpinner /> }}
          locale={TableLocale}
          scroll={{ x: true }}
        />
        {activeTabKey === "hedis" && (
          <div className="measure-report__body__footer">
            <span className="measure-report__body__required-star">*</span>
            <span className="measure-report__body__footer__label">A lower rate indicates better performance for this indicator</span>
          </div>
        )}
      </div>
    </div>
  );
};
export default MeasureReport;
